import {assert} from 'Asserts';
import {ClientId} from 'notebook/ELNConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  BaseStructuredTable,
  CreateStructuredTableProps,
  CustomEvalTableView,
  GetCustomEvalArgsOptions,
  LastSyncedUpstreamValue,
  SchemaAndColumnGroup,
  StructuredTableAdapterOptions,
  StructuredTableColumn,
  StructuredTableKind,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  BaseTypedValueType,
  Cell,
  CellIndex,
  CustomEvalFnAndType,
  CustomEvalResult,
  CustomEvalType,
  RowMetadata,
  TableColumnType,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {ProcedureValidationError} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  getLibraryDefinitionTypedValue,
  libraryDefinitionToTypedValue,
} from 'procedures/parameter-library/libraryParameterDefinitionTypedValueUtils';
import {
  ParameterUsage,
  ParameterUsages,
  ProcedureDataParameterSetting,
} from 'procedures/ProcedureDomainTypes';
import {DEFAULT_DATA_PARAMETER_TYPE} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {convertDataParameterTypeToTypedValue} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldTypeConversionUtils';
import BaseProcedureDataParametersConfigTableAdapter, {
  BaseProcedureDataParametersConfigTableRowMetadata,
  buildCoreDataParameterSetting,
  columnProvenanceId,
  DataParameterConfigColumn,
  getColumnIndex,
  isDataParameterSettingEmpty,
  ProcedureDataParametersConfigTableMetadata,
} from 'procedures/tables/BaseProcedureDataParametersConfigTableAdapter';
import {
  BaseParameterConfigTableMetadata,
  getParameterUsagesForId,
} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import {LibraryParameterDefinitionConfigTableResolvedColumn} from 'procedures/tables/ProcedureTableConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {Optional} from 'util/Types';

export type ProcedureDataParametersConfigWithParamLibraryTableMetadata = BaseParameterConfigTableMetadata;

export interface ProcedureDataParametersConfigWithParamLibraryTableRowMetadata extends BaseProcedureDataParametersConfigTableRowMetadata {
  libraryParameterDefinitionId?: string | null;
}

export type ProcedureDataParametersConfigWithParamLibraryTable = BaseStructuredTable<
  ProcedureDataParametersConfigWithParamLibraryTableMetadata,
  ProcedureDataParametersConfigWithParamLibraryTableRowMetadata,
  ClientId
>;

/**
 * Computes the Description cell from the selected library parameter (name column).
 * `cellDependencies[0]` is the name column’s typed value; description text is loaded via custom resolution.
 * The cell is read-only.
 */
function descriptionColumnCustomEval(
  descriptionTypedValue: StringTypedValue,
  _columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: {
    tableMetadata: ProcedureDataParametersConfigWithParamLibraryTableMetadata;
    rowMetadata: ProcedureDataParametersConfigWithParamLibraryTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  const libDefinitionTypedValue = getLibraryDefinitionTypedValue(cellDependencies[0]);
  // Use empty string if no library definition is present
  if (libDefinitionTypedValue == null) {
    return {
      val: {type: BaseTypedValueType.STRING as 'STRING', rawValue: ''},
      error: null,
      locked: true,
    };
  }
  const {resolvedValue, error} = resolveFromResolutionQuery(
    descriptionTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.LIBRARY_PARAMETER_DEFINITION_TABLE_COLUMN,
      libraryParameterDefinitionId: libDefinitionTypedValue.id,
      column: LibraryParameterDefinitionConfigTableResolvedColumn.DESCRIPTION,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: true};
}

/**
 * Computes the Type cell from the selected library parameter (name column).
 * Maps the library definition’s data type to a procedure schema-field typed value via custom resolution;
 * if no library definition is present, uses {@link DEFAULT_DATA_PARAMETER_TYPE}.
 * The cell is read-only.
 */
function dataTypeCustomEval(
  dataTypeTypedValue: TypedValue,
  _columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: {
    tableMetadata: ProcedureDataParametersConfigWithParamLibraryTableMetadata;
    rowMetadata: ProcedureDataParametersConfigWithParamLibraryTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  const libDefinitionTypedValue = getLibraryDefinitionTypedValue(cellDependencies[0]);
  // Use default data parameter type if no library definition is present
  if (libDefinitionTypedValue == null) {
    return {
      val: convertDataParameterTypeToTypedValue(DEFAULT_DATA_PARAMETER_TYPE),
      error: null,
      locked: true,
    };
  }
  const {resolvedValue, error} = resolveFromResolutionQuery(
    dataTypeTypedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.LIBRARY_PARAMETER_DEFINITION_TABLE_COLUMN,
      libraryParameterDefinitionId: libDefinitionTypedValue.id,
      column: LibraryParameterDefinitionConfigTableResolvedColumn.TYPE,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: true};
}

/**
 * Validates that the selected unit belongs to the unit type defined in the library parameter definition.
 * `cellDependencies[0]` is the name column's typed value (carries the library definition id).
 * If the library definition has no unit type restriction, the cell is valid.
 * If the library definition specifies a unit type, leaving the unit empty will result in a validation error.
 */
function unitColumnCustomEval(
  typedValue: TypedValue,
  _columnDefinition: Optional<ColumnDefinition>,
  cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: {
    tableMetadata: ProcedureDataParametersConfigWithParamLibraryTableMetadata;
    rowMetadata: ProcedureDataParametersConfigWithParamLibraryTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn,
  _resolveTypedValueToType: ResolveTypedValueToTypeFn,
  _fileId?: string | null
): CustomEvalResult {
  const defaultResult: CustomEvalResult = {val: typedValue, error: null, locked: false};

  const libDefinitionTypedValue = getLibraryDefinitionTypedValue(cellDependencies[0]);
  // If no library definition is selected yet, we can't do unit validation.
  if (libDefinitionTypedValue == null) {
    return defaultResult;
  }

  const unitId = typedValue?.type === BaseTypedValueType.UNIT_REFERENCE ? typedValue.unitSummary.id : null;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType: CustomResolutionQueryType.PROCEDURE_PARAMETER_VALIDATE_UNIT_TYPE,
      libraryParameterDefinitionId: libDefinitionTypedValue.id,
      unitId,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue ?? typedValue, error, locked: false};
}

export default class ProcedureDataParametersConfigWithParamLibraryTableAdapter extends BaseProcedureDataParametersConfigTableAdapter<ProcedureDataParametersConfigWithParamLibraryTableRowMetadata> {
  constructor(options?: StructuredTableAdapterOptions) {
    super(StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG_WITH_PARAM_LIBRARY, options);
  }

  getBlankRowMetadata(
    _tableMetadata: ProcedureDataParametersConfigTableMetadata
  ): ProcedureDataParametersConfigWithParamLibraryTableRowMetadata {
    return {
      apiId: null,
      procedureDataParameterId: null,
      plannedValueId: null,
      libraryParameterDefinitionId: null,
    };
  }

  static getAdapter(): ProcedureDataParametersConfigWithParamLibraryTableAdapter {
    return getStructuredTableAdapters()[
      StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG_WITH_PARAM_LIBRARY
    ] as ProcedureDataParametersConfigWithParamLibraryTableAdapter;
  }

  /**
   * Builds the same enabled columns as the base config table, but sets the Name column
   * to {@link TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR} so the name is chosen
   * from the parameter library instead of free text.
   */
  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: ProcedureDataParametersConfigTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const columns = super._getAllSupportedColumns({
      entryId,
      metadata,
      schemas,
      selectorsById,
      structuredTableInsertionContext,
    });
    const nameColIndex = getColumnIndex(DataParameterConfigColumn.NAME, columns);
    assert(nameColIndex !== -1, 'Name column not found');
    columns[nameColIndex] = {
      ...columns[nameColIndex],
      columnType: TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR,
    };
    return columns;
  }

  override getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [...super.getNonFinalizingColumnTypes(), TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR];
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: ProcedureDataParametersConfigWithParamLibraryTable,
    row: StructuredTableRow<ProcedureDataParametersConfigWithParamLibraryTableRowMetadata>
  ): boolean {
    const setting = ProcedureDataParametersConfigWithParamLibraryTableAdapter.rowToDataParameterSetting(
      structuredTable.columns,
      row
    );
    return isDataParameterSettingEmpty(setting);
  }

  override getTableSpecificCustomEvalFn(
    tableView: CustomEvalTableView<
      ProcedureDataParametersConfigWithParamLibraryTableMetadata,
      ProcedureDataParametersConfigWithParamLibraryTableRowMetadata
    >,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: ProcedureDataParametersConfigWithParamLibraryTableMetadata;
    rowMetadata: ProcedureDataParametersConfigWithParamLibraryTableRowMetadata;
  }> | null {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    switch (column.provenanceUuid) {
      case columnProvenanceId(DataParameterConfigColumn.DESCRIPTION):
        return {customEvalFn: descriptionColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case columnProvenanceId(DataParameterConfigColumn.TYPE):
        return {customEvalFn: dataTypeCustomEval, customEvalType: CustomEvalType.DEFAULT};
      case columnProvenanceId(DataParameterConfigColumn.UNITS):
        return {customEvalFn: unitColumnCustomEval, customEvalType: CustomEvalType.DEFAULT};
      default:
        return null;
    }
  }

  override getTableSpecificCustomEvalArgs(
    tableView: CustomEvalTableView<
      ProcedureDataParametersConfigWithParamLibraryTableMetadata,
      ProcedureDataParametersConfigWithParamLibraryTableRowMetadata
    >,
    cellIndex: CellIndex,
    {toSpreadsheetIndex}: GetCustomEvalArgsOptions
  ): Array<string> {
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    const isDependent =
      column.provenanceUuid === columnProvenanceId(DataParameterConfigColumn.DESCRIPTION) ||
      column.provenanceUuid === columnProvenanceId(DataParameterConfigColumn.TYPE) ||
      column.provenanceUuid === columnProvenanceId(DataParameterConfigColumn.UNITS);
    if (!isDependent) {
      return [];
    }
    const nameColumnIndex = tableView.columns.findIndex(
      (column) => column.provenanceUuid === columnProvenanceId(DataParameterConfigColumn.NAME)
    );
    assert(nameColumnIndex !== -1, 'Name column not found');
    return [`=${toSpreadsheetIndex({row: cellIndex.row, column: nameColumnIndex})}`];
  }

  static override makeCellFromDataParameter(
    dataParameterSetting: ProcedureDataParameterSetting,
    column: StructuredTableColumn,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>
  ): Cell {
    const libraryParameterDefinition = dataParameterSetting.dataParameterVersion.libraryParameterDefinition;

    switch (column.provenanceUuid) {
      case columnProvenanceId(DataParameterConfigColumn.NAME): {
        const nameCell = BaseProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
          dataParameterSetting,
          column,
          validationErrors,
          parameterUsages
        );
        if (libraryParameterDefinition != null) {
          return {
            ...nameCell,
            typedValue: libraryDefinitionToTypedValue(libraryParameterDefinition),
          };
        }
        return nameCell;
      }
      case columnProvenanceId(DataParameterConfigColumn.DESCRIPTION):
      case columnProvenanceId(DataParameterConfigColumn.TYPE):
        return {
          ...BaseProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
            dataParameterSetting,
            column,
            validationErrors,
            parameterUsages
          ),
          locked: true,
        };
      default:
        return BaseProcedureDataParametersConfigTableAdapter.makeCellFromDataParameter(
          dataParameterSetting,
          column,
          validationErrors,
          parameterUsages
        );
    }
  }

  static dataParameterToRow(
    dataParameterSetting: ProcedureDataParameterSetting,
    table: ProcedureDataParametersConfigWithParamLibraryTable,
    rowIndex: number,
    validationErrors: Array<ProcedureValidationError>,
    parameterUsages: Array<ParameterUsage>,
    _cellLockedMessage?: Optional<string>
  ): StructuredTableRow<ProcedureDataParametersConfigWithParamLibraryTableRowMetadata> {
    const adapter = ProcedureDataParametersConfigWithParamLibraryTableAdapter.getAdapter();

    return {
      ...adapter.getBlankRow(table.metadata, rowIndex, table.columns),
      rowId: dataParameterSetting.dataParameterVersion.cid,
      metadata: {
        apiId: dataParameterSetting.dataParameterVersion.id,
        procedureDataParameterId: dataParameterSetting.dataParameterVersion.procedureDataParameterId,
        plannedValueId: dataParameterSetting.values.length > 0 ? dataParameterSetting.values[0].id : null,
        libraryParameterDefinitionId:
          dataParameterSetting.dataParameterVersion.libraryParameterDefinition?.id ?? null,
      },
      cells: table.columns.map((column) =>
        ProcedureDataParametersConfigWithParamLibraryTableAdapter.makeCellFromDataParameter(
          dataParameterSetting,
          column,
          validationErrors,
          parameterUsages
        )
      ),
    };
  }

  /**
   * Converts a config table row back into a {@link ProcedureDataParameterSetting}. Starts from
   * {@link buildCoreDataParameterSetting}. When the name cell carries a library-definition typed
   * value, sets `libraryParameterDefinition` (id and name) from that definition.
   */
  static rowToDataParameterSetting(
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ProcedureDataParametersConfigWithParamLibraryTableRowMetadata>
  ): ProcedureDataParameterSetting {
    const coreSetting = buildCoreDataParameterSetting(columns, row);
    const nameCell = row.cells[getColumnIndex(DataParameterConfigColumn.NAME, columns)];
    const libDefTypedValue = getLibraryDefinitionTypedValue(nameCell.typedValue);

    if (libDefTypedValue == null) {
      return coreSetting;
    }

    const {dataParameterVersion} = coreSetting;

    // TODO (BNCH-132851): Ensure data type is correct here, based on the library parameter definition
    return {
      ...coreSetting,
      dataParameterVersion: {
        ...dataParameterVersion,
        libraryParameterDefinition: {
          id: libDefTypedValue.id,
          name: libDefTypedValue.name,
        },
      },
    };
  }

  static makeLastSyncedUpstreamValues(
    this: void,
    dataParameterSetting: ProcedureDataParameterSetting,
    row: StructuredTableRow<ProcedureDataParametersConfigWithParamLibraryTableRowMetadata>,
    columns: Array<StructuredTableColumn>,
    parameterUsages: Array<ParameterUsage>
  ): Array<LastSyncedUpstreamValue> {
    const submittedAt = new Date().toISOString();
    return columns.map((column) => {
      const delegatedColumnProvenanceId = (row.metadata as RowMetadata).delegatedColumns?.[
        column.provenanceUuid
      ];
      return {
        columnGroup: null,
        columnType: column.columnType,
        columnProvenanceUuid: delegatedColumnProvenanceId ?? column.provenanceUuid,
        submittedAt,
        fieldId: null,
        value:
          ProcedureDataParametersConfigWithParamLibraryTableAdapter.makeCellFromDataParameter(
            dataParameterSetting,
            column,
            [],
            parameterUsages
          ).typedValue ?? null,
      };
    });
  }

  createTableFromDataParameters(
    createProps: CreateStructuredTableProps<ProcedureDataParametersConfigTableMetadata>,
    tableCid: string,
    dataParameters: Array<ProcedureDataParameterSetting>,
    procedureValidationErrors: Array<ProcedureValidationError>,
    parameterUsagesByParameterVersionId: ParameterUsages
  ): ProcedureDataParametersConfigWithParamLibraryTable {
    const baseTable = {
      ...this.createTable(createProps),
      cid: tableCid,
    };
    return {
      ...baseTable,
      rows: dataParameters.map((d, index) =>
        ProcedureDataParametersConfigWithParamLibraryTableAdapter.dataParameterToRow(
          d,
          baseTable as ProcedureDataParametersConfigWithParamLibraryTable,
          index,
          procedureValidationErrors,
          getParameterUsagesForId(parameterUsagesByParameterVersionId, d.dataParameterVersion.id),
          undefined
        )
      ),
    };
  }
}
